import { useRef } from 'react';
import clsx from 'clsx';

/**
 * Six-box one-time-code entry. Controlled component — pass `value` (string)
 * and `onChange` (receives the next full string, digits only, length-capped).
 * Wire up via react-hook-form's <Controller/> rather than `register`, since
 * this isn't a single native <input>.
 */
export default function OtpInput({ length = 6, value = '', onChange, error, autoFocus = true, disabled }) {
  const boxRefs = useRef([]);
  const digits = value.split('').concat(Array(length).fill('')).slice(0, length);

  const setDigit = (index, digit) => {
    const next = digits.slice();
    next[index] = digit;
    onChange?.(next.join(''));
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      setDigit(index, '');
      return;
    }
    // Handles fast typing/autofill landing multiple characters in one box.
    const chars = raw.split('');
    const next = digits.slice();
    let i = index;
    for (const ch of chars) {
      if (i >= length) break;
      next[i] = ch;
      i += 1;
    }
    onChange?.(next.join('').slice(0, length));
    const focusIndex = Math.min(i, length - 1);
    boxRefs.current[focusIndex]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      boxRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) boxRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < length - 1) boxRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange?.(pasted.padEnd(length, '').slice(0, length).replace(/ /g, ''));
    const focusIndex = Math.min(pasted.length, length - 1);
    boxRefs.current[focusIndex]?.focus();
  };

  return (
    <div>
      <div className="flex gap-2 justify-center">
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (boxRefs.current[i] = el)}
            value={digit}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            autoFocus={autoFocus && i === 0}
            disabled={disabled}
            inputMode="numeric"
            maxLength={1}
            aria-label={`Digit ${i + 1}`}
            className={clsx(
              'h-12 w-10 sm:h-14 sm:w-12 rounded-xl border bg-background-elevated text-center text-xl font-semibold text-foreground',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent transition-colors',
              error ? 'border-danger' : 'border-border focus:border-border-strong'
            )}
          />
        ))}
      </div>
      {error && <p className="mt-2 text-xs text-danger text-center">{error}</p>}
    </div>
  );
}
